/**
 * ESPELHO de `core/backend/src/modules/job-description/domain/form-rules.ts` (DEV-258).
 * O Core é a autoridade (revalida no PATCH e no envio); este espelho existe
 * para a tela validar ao sair do campo. Mude os dois juntos — os testes deste
 * diretório repetem os casos do Core.
 */
/**
 * DEV-260 — regras de TELA do formulário da pessoa gestora, sobre os `name`
 * estáveis do template do DEV-269. Uma só implementação no Core; o SPA tem o
 * espelho testado com os mesmos casos.
 *
 * As comparações de rótulo normalizam caixa/acento porque as `choices` do
 * template e do Figma ainda divergem (research.md D14) — a regra tem de valer
 * para os dois conjuntos até o template ser corrigido.
 */

export type Answers = Record<string, unknown>;

export function normalizeLabel(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

const BASIC_EDUCATION = new Set(['ensino fundamental', 'ensino medio']);

export function isRemoteWorkModel(label: string): boolean {
  return normalizeLabel(label) === 'remoto';
}

export function isRangeDisclosure(label: string): boolean {
  return normalizeLabel(label).includes('faixa');
}

export function isExactDisclosure(label: string): boolean {
  return normalizeLabel(label).includes('exato');
}

export function hasTechnicalOrHigherEducation(types: string[]): boolean {
  return types.some((t) => !BASIC_EDUCATION.has(normalizeLabel(t)));
}

export function isDisabilityCriterion(label: string): boolean {
  const n = normalizeLabel(label);
  return n === 'pcd' || n.includes('deficiencia');
}

/** "Qualquer uma" em tipo de deficiência (AC-55). */
export function isAnyDisability(label: string): boolean {
  return normalizeLabel(label) === 'qualquer uma';
}

/** Governante → dependentes (AC-25/32/40/42/54). Governante oculto leva os dependentes (AC-86b). */
export const CONDITIONAL_DEPENDENTS: Record<string, readonly string[]> = {
  modelo_trabalho: [
    'local_trabalho',
    'requisitos_localidade_estado',
    'requisitos_localidade_cidade',
  ],
  tipo_divulgacao_salario: ['valor_minimo', 'valor_maximo', 'valor_exato'],
  tipos_formacao: ['aceita_todos_cursos', 'areas_curso', 'cursos'],
  aceita_todos_cursos: ['areas_curso', 'cursos'],
  criterios_diversidade: ['tipo_deficiencia'],
};

/**
 * A pergunta está à mostra dadas as respostas atuais? Só fala das condicionais
 * de tela; visibilidade pela configuração do programa é da projeção.
 */
export function isQuestionShown(name: string, answers: Answers): boolean {
  switch (name) {
    case 'local_trabalho':
    case 'requisitos_localidade_estado':
    case 'requisitos_localidade_cidade': {
      const model = asString(answers.modelo_trabalho);
      return model !== null && !isRemoteWorkModel(model);
    }
    case 'valor_minimo':
    case 'valor_maximo': {
      const d = asString(answers.tipo_divulgacao_salario);
      return d !== null && isRangeDisclosure(d);
    }
    case 'valor_exato': {
      const d = asString(answers.tipo_divulgacao_salario);
      return d !== null && isExactDisclosure(d);
    }
    case 'aceita_todos_cursos':
      return hasTechnicalOrHigherEducation(asStringArray(answers.tipos_formacao));
    case 'areas_curso':
    case 'cursos':
      return (
        isQuestionShown('aceita_todos_cursos', answers) && answers.aceita_todos_cursos === false
      );
    case 'tipo_deficiencia':
      return asStringArray(answers.criterios_diversidade).some(isDisabilityCriterion);
    default:
      return true;
  }
}

/**
 * Condicionais internas que, quando à mostra, são exigidas nos tipos comuns
 * mesmo sem `isRequired` no template (AC-25 local, AC-32 valores, AC-40 pergunta
 * de cursos, AC-56 tipo de deficiência com PCD). A regra de grupo de áreas/cursos
 * (AC-46) é tratada na validação, não aqui.
 */
export const CONDITIONALLY_REQUIRED = [
  'local_trabalho',
  'valor_minimo',
  'valor_maximo',
  'valor_exato',
  'aceita_todos_cursos',
  'tipo_deficiencia',
] as const;
